import { GeotabError, Group, User, UserAuthenticationType } from "@fairfleet/geotab";
import { TFunction } from "i18next";
import { ROOT_SECURITY_GROUP_ID } from "@/constants/geotabGroupIds";
import { getErrorMessage } from "@/utilities/error/getErrorMessage";
import { GroupIndex, isUnderBranch } from "@/utilities/groupIndex";

export const MAX_ACTIVE_TO = new Date("2050-01-01T00:00:00.000Z");

export function isArchived(user: User): boolean {
  const activeTo = new Date(user.activeTo ?? MAX_ACTIVE_TO);
  return activeTo.getTime() <= Date.now();
}

export function mapGroupReferences(groups: Group[]): Group[] {
  return groups.map(group => ({ id: group.id }) as Group);
}

export function asGroupArray(value: unknown): Group[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is Group => !!entry && typeof entry === "object");
}

export function getPrimarySecurityGroupId(groups: Group[] | undefined): string | null {
  const values = groups ?? [];
  const preferredGroup = values.find(group => group.id !== ROOT_SECURITY_GROUP_ID);
  return preferredGroup?.id ?? values[0]?.id ?? null;
}

export function mergeUserForSet(fresh: User, changes: Partial<User>): User {
  const filtered: Partial<User> = {};
  for (const [key, value] of Object.entries(changes)) {
    if (value !== undefined) {
      (filtered as Record<string, unknown>)[key] = value;
    }
  }
  return { ...fresh, ...filtered, id: fresh.id } as User;
}

export function sanitizeMaxPcDistance(raw: string): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

/**
 * The clearances `me` may hand out: their own and every clearance below one of them.
 *
 * @param index - The group tree, as built by `buildGroupIndex`.
 */
export function getAssignableClearanceIds(
  me: User | null,
  securityGroups: Group[],
  index: Pick<GroupIndex, "parentById">,
): Set<string> {
  const myIds = (me?.securityGroups ?? [])
    .map(group => group.id)
    .filter(id => id !== ROOT_SECURITY_GROUP_ID);

  const result = new Set<string>();
  for (const group of securityGroups) {
    if (group.id === ROOT_SECURITY_GROUP_ID) continue;
    if (myIds.some(myId => isUnderBranch(index, group.id, myId))) result.add(group.id);
  }
  return result;
}

export function canManageUser(target: User, assignable: Set<string>): boolean {
  const targetGroupIds = (target.securityGroups ?? [])
    .filter(g => g.id !== ROOT_SECURITY_GROUP_ID)
    .map(g => g.id);

  if (targetGroupIds.length === 0) {
    return assignable.size > 0;
  }

  return targetGroupIds.every(id => assignable.has(id));
}

export function isSameUser(target: User | null | undefined, me: User | null): boolean {
  return target != null && me != null && target.id === me.id;
}

export function passwordForAdd(password: string): string | undefined {
  return password.trim().length > 0 ? password : undefined;
}

export type UserWriteDenial = "cannotManageUser" | "selfProtected" | "validationClearance";

export function checkUserCreate(
  nextSecurityGroupIds: string[],
  assignable: Set<string>,
): UserWriteDenial | null {
  if (nextSecurityGroupIds.some(id => !assignable.has(id))) return "validationClearance";
  return null;
}

export function checkUserUpdate(
  fresh: User,
  nextSecurityGroupIds: string[],
  me: User | null,
  assignable: Set<string>,
): UserWriteDenial | null {
  if (!canManageUser(fresh, assignable)) return "cannotManageUser";
  const currentIds = new Set((fresh.securityGroups ?? []).map(group => group.id));
  const nextIds = new Set(nextSecurityGroupIds);
  const clearanceChanged =
    currentIds.size !== nextIds.size || [...nextIds].some(id => !currentIds.has(id));
  if (clearanceChanged && isSameUser(fresh, me)) return "selfProtected";
  if ([...nextIds].some(id => !assignable.has(id) && !currentIds.has(id)))
    return "validationClearance";
  return null;
}

export function shouldApplySaveSelection(
  started: { creating: boolean; selectedUserId: string | null },
  current: { creating: boolean; selectedUserId: string | null },
): boolean {
  return started.creating === current.creating && started.selectedUserId === current.selectedUserId;
}

/** All editable fields of the user detail form. */
export interface UserForm {
  name: string;
  firstName: string;
  lastName: string;
  designation: string;
  employeeNo: string;
  phoneNumber: string;
  phoneExt: string;
  comment: string;
  companyName: string;
  companyAddress: string;
  countryCode: string;
  carrierNumber: string;
  language: string;
  dateFormat: string;
  timeZoneId: string;
  defaultPage: string;
  isDriver: boolean;
  isMetric: boolean;
  isEmailReportEnabled: boolean;
  isNewsEnabled: boolean;
  isServiceUpdatesEnabled: boolean;
  authorityName: string;
  authorityAddress: string;
  isAdverseDrivingEnabled: boolean;
  isExemptHOSEnabled: boolean;
  isPersonalConveyanceEnabled: boolean;
  isYardMoveEnabled: boolean;
  maxPCDistancePerDay: number;
  changePassword: boolean;
  userAuthenticationType: UserAuthenticationType;
  windowsAuthenticationUserId: string;
  password: string;
  companyGroups: Group[];
  reportGroups: Group[];
  securityGroups: Group[];
  selectedClearanceId: string | null;
}

/**
 * Form for "no selection" and "create user". Creating also defaults the start page, forces a
 * password change and pre-selects the company group.
 */
export function emptyUserForm(opts: {
  isDriver: boolean;
  creating: boolean;
  companyDefault?: Group;
}): UserForm {
  return {
    name: "",
    firstName: "",
    lastName: "",
    designation: "",
    employeeNo: "",
    phoneNumber: "",
    phoneExt: "",
    comment: "",
    companyName: "",
    companyAddress: "",
    countryCode: "",
    carrierNumber: "",
    language: "en",
    dateFormat: "",
    timeZoneId: "",
    defaultPage: opts.creating ? "dashboard" : "",
    isDriver: opts.isDriver,
    isMetric: true,
    isEmailReportEnabled: true,
    isNewsEnabled: true,
    isServiceUpdatesEnabled: false,
    authorityName: "",
    authorityAddress: "",
    isAdverseDrivingEnabled: false,
    isExemptHOSEnabled: false,
    isPersonalConveyanceEnabled: false,
    isYardMoveEnabled: false,
    maxPCDistancePerDay: 0,
    changePassword: opts.creating,
    userAuthenticationType: "BasicAuthentication",
    windowsAuthenticationUserId: "",
    password: "",
    companyGroups: opts.creating && opts.companyDefault ? [opts.companyDefault] : [],
    reportGroups: [],
    securityGroups: [],
    selectedClearanceId: null,
  };
}

/** Form values of an existing user; group references are replaced by the loaded group objects. */
export function userToForm(user: User, groupById: Map<string, Group>): UserForm {
  const resolve = (groups: Group[] | undefined) =>
    (groups ?? []).map(group => groupById.get(group.id) ?? group);
  return {
    name: user.name ?? "",
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    designation: user.designation ?? "",
    employeeNo: user.employeeNo ?? "",
    phoneNumber: user.phoneNumber ?? "",
    phoneExt: user.phoneNumberExtension ?? "",
    comment: user.comment ?? "",
    companyName: user.companyName ?? "",
    companyAddress: user.companyAddress ?? "",
    countryCode: user.countryCode ?? "",
    carrierNumber: user.carrierNumber ?? "",
    language: user.language ?? "en",
    dateFormat: user.dateFormat ?? "",
    timeZoneId: user.timeZoneId ?? "",
    defaultPage: user.defaultPage ?? "",
    isDriver: !!user.isDriver,
    isMetric: user.isMetric ?? true,
    isEmailReportEnabled: user.isEmailReportEnabled ?? true,
    isNewsEnabled: user.isNewsEnabled ?? true,
    isServiceUpdatesEnabled: user.isServiceDisruptionNotificationsEnabled ?? false,
    authorityName: user.authorityName ?? "",
    authorityAddress: user.authorityAddress ?? "",
    isAdverseDrivingEnabled: user.isAdverseDrivingEnabled ?? false,
    isExemptHOSEnabled: user.isExemptHOSEnabled ?? false,
    isPersonalConveyanceEnabled: user.isPersonalConveyanceEnabled ?? false,
    isYardMoveEnabled: user.isYardMoveEnabled ?? false,
    maxPCDistancePerDay: user.maxPCDistancePerDay ?? 0,
    changePassword: !!user.changePassword,
    userAuthenticationType: user.userAuthenticationType ?? "BasicAuthentication",
    windowsAuthenticationUserId: user.windowsAuthenticationUserId ?? "",
    password: "",
    companyGroups: resolve(user.companyGroups),
    reportGroups: resolve(user.reportGroups),
    securityGroups: resolve(user.securityGroups),
    selectedClearanceId: getPrimarySecurityGroupId(user.securityGroups),
  };
}

/** Fields written on both create and update (Geotab `User` property names). */
export function userFormToPayload(form: UserForm): Record<string, unknown> {
  return {
    name: form.name.trim(),
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    designation: form.designation.trim(),
    employeeNo: form.employeeNo.trim(),
    phoneNumber: form.phoneNumber.trim(),
    phoneNumberExtension: form.phoneExt.trim(),
    companyName: form.companyName.trim(),
    companyAddress: form.companyAddress.trim(),
    countryCode: form.countryCode.trim().toUpperCase(),
    carrierNumber: form.carrierNumber.trim(),
    language: form.language.trim(),
    dateFormat: form.dateFormat.trim(),
    timeZoneId: form.timeZoneId.trim(),
    defaultPage: form.defaultPage.trim(),
    comment: form.comment,
    isDriver: form.isDriver,
    isMetric: form.isMetric,
    isEmailReportEnabled: form.isEmailReportEnabled,
    isNewsEnabled: form.isNewsEnabled,
    isServiceDisruptionNotificationsEnabled: form.isServiceUpdatesEnabled,
    authorityName: form.authorityName.trim(),
    authorityAddress: form.authorityAddress.trim(),
    isAdverseDrivingEnabled: form.isAdverseDrivingEnabled,
    isExemptHOSEnabled: form.isExemptHOSEnabled,
    isPersonalConveyanceEnabled: form.isPersonalConveyanceEnabled,
    isYardMoveEnabled: form.isYardMoveEnabled,
    maxPCDistancePerDay: form.maxPCDistancePerDay,
    companyGroups: mapGroupReferences(form.companyGroups),
    reportGroups: mapGroupReferences(form.reportGroups),
    securityGroups: mapGroupReferences(form.securityGroups),
  };
}

/**
 * Sign-in fields written on both create and update. The Windows user name goes only with Windows
 * sign-in: Geotab rejects an empty one with "User Name cannot be empty", even for a password user.
 */
export function userFormToAuthFields(form: UserForm): Record<string, unknown> {
  return {
    changePassword: form.changePassword,
    userAuthenticationType: form.userAuthenticationType,
    ...(form.userAuthenticationType === "WindowsAuthentication"
      ? { windowsAuthenticationUserId: form.windowsAuthenticationUserId.trim() }
      : {}),
  };
}

export type SetUserField = <K extends keyof UserForm>(key: K, value: UserForm[K]) => void;

/** Geotab error types (`data.type`) that have a translated message on this page. */
const GEOTAB_ERROR_KEYS: Record<string, string> = {
  DuplicateUserException: "users.duplicateUserName",
};

/** The user's language for Geotab errors known here, Geotab's raw text for the rest. */
export function userWriteErrorMessage(t: TFunction, error: unknown): string {
  if (error instanceof GeotabError) {
    const type = (error.data as { type?: unknown } | undefined)?.type;
    const key = typeof type === "string" ? GEOTAB_ERROR_KEYS[type] : undefined;
    if (key) return t(key);
  }
  return getErrorMessage(error);
}
